import React, { useState } from 'react';
import { ActivityIndicator, Linking, ScrollView, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import api from '../../../../utils/api';
import { Icon } from '../../../../components/Icon';
import { Illustration } from '../../../../components/Illustration';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';
import useAccessServiceTypes from '@/hooks/useAccessServiceTypes';
import { NativeSelect } from '../Tickets/TicketFilters.web';

import NewAccessAuthorizationModal from './NewAccessAuthorizationModal.web';
import AccessAuthorizationDetailModal from './AccessAuthorizationDetailModal.web';

dayjs.locale('es');

const HELP_URL = 'https://ding-dong.mx/soporte/administradores/accesos/autorizaciones-de-acceso/';

type Tab = 'pending' | 'approved' | 'suspended' | 'rejected' | 'expired';


export default function AccessAuthorizationsDashboard() {
  const { communityId } = useActiveAdminCommunity();
  const queryClient = useQueryClient();
  const navigation = useNavigation<any>();

  const [tab, setTab] = useState<Tab>('pending');
  const [showNew, setShowNew] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  // 'all' o el uuid de un tipo del catálogo
  const [serviceType, setServiceType] = useState('all');
  const { types: serviceTypes } = useAccessServiceTypes(communityId);
  const filterParams = serviceType !== 'all' ? { service_type_uuid: serviceType } : {};

  const countsQuery = useQuery<any>({
    queryKey: ['access-authorizations.counts', communityId, serviceType],
    queryFn: () =>
      api.get(`/communities/${communityId}/access-authorizations/counts`, { params: filterParams }).then(r => r.data),
    enabled: !!communityId,
    placeholderData: keepPreviousData,
  });

  const listQuery = useQuery<any>({
    queryKey: ['access-authorizations', communityId, tab, serviceType],
    queryFn: () =>
      api.get(`/communities/${communityId}/access-authorizations`, { params: { status: tab, per_page: 100, ...filterParams } })
        .then(r => r.data),
    enabled: !!communityId,
    placeholderData: keepPreviousData,
  });

  const counts = countsQuery.data ?? {};

  const items: any[] = listQuery.data?.data ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['access-authorizations', communityId] });
    queryClient.invalidateQueries({ queryKey: ['access-authorizations.counts', communityId] });
  };

  return (
    <View style={tw`flex-1`}>
      {/* Header */}
      <View style={tw`flex-row items-center justify-between mb-4`}>
        <View style={tw`flex-row items-center`}>
          <Illustration name="autorizacion" width={40} height={40} style={tw`mr-3`} />
          <View>
            <Text style={tw`text-secondary font-600 text-base mb-1`}>Autorizaciones de acceso</Text>
            <View style={tw`self-start bg-white border border-gray-200 rounded-full px-3 py-1`}>
              <Text style={tw`text-gray-500 text-sm`}>{dayjs().format('dddd D [de] MMMM')}</Text>
            </View>
          </View>
        </View>

        <View style={tw`flex-row items-center gap-4`}>
          <View style={tw`bg-white border border-gray-200 rounded-2xl px-5 py-3 flex-row items-center gap-x-6`}>
            <View style={tw`flex-row items-center`}>
              <Icon name="qrLeidos" size={16} style={tw`mr-2`} />
              <Text style={tw`text-primary font-500 mr-2`}>Avaladas</Text>
              <Text style={tw`text-gray-800 font-600 text-lg`}>{counts.approved ?? 0}</Text>
            </View>
            <View style={tw`w-px h-6 bg-gray-200`} />
            <View style={tw`flex-row items-center`}>
              <Icon name="qrRechazado" size={15} style={tw`mr-2`} />
              <Text style={tw`text-red font-500 mr-2`}>Rechazadas</Text>
              <Text style={tw`text-gray-800 font-600 text-lg`}>{counts.rejected ?? 0}</Text>
            </View>
          </View>

          <HeaderIconButton
            icon="help-circle-outline"
            label="Ayuda"
            onPress={() => Linking.openURL(HELP_URL)}
          />
          <HeaderIconButton
            icon="settings-outline"
            label="Configuración de autorizaciones"
            onPress={() => navigation.navigate('Admin.Settings.ProviderAuth')}
          />

          <TouchableOpacity
            onPress={() => setShowNew(true)}
            style={tw`bg-secondary px-5 py-3 rounded-full flex-row items-center`}
          >
            <Icon name="add" size={18} style={tw`mr-2`} />
            <Text style={tw`text-white font-500 text-sm`}>Nueva autorización</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Contenedor de tabs + tabla */}
      <View style={tw`flex-1 bg-white rounded-2xl border border-gray-200 overflow-hidden`}>
        <View style={tw`flex-row items-center justify-between pr-5`}>
          <AuthorizationStatusTabs value={tab} onChange={setTab} counts={counts} />

          {/* Aunque el catálogo esté apagado: sirve para filtrar el historial */}
          {serviceTypes.length > 0 && (
            <View style={tw`pt-2`}>
              <NativeSelect
                value={serviceType}
                defaultValue="all"
                onChange={setServiceType}
                options={[
                  { value: 'all', label: 'Todos los servicios' },
                  ...serviceTypes.map(t => ({ value: t.uuid, label: t.name })),
                ]}
              />
            </View>
          )}
        </View>

        <AuthorizationsTable
          tab={tab}
          items={items}
          isLoading={listQuery.isLoading}
          onRowPress={(a) => setSelectedUuid(a.uuid)}
        />
      </View>

      {showNew && (
        <NewAccessAuthorizationModal
          communityId={communityId}
          onRequestClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); invalidate(); }}
        />
      )}

      {selectedUuid && (
        <AccessAuthorizationDetailModal
          communityId={communityId}
          authorizationUuid={selectedUuid}
          onRequestClose={() => setSelectedUuid(null)}
          onChanged={invalidate}
        />
      )}
    </View>
  );
}


function HeaderIconButton({ icon, label, onPress }: { icon: any; label: string; onPress: () => void }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={label}
      // @ts-ignore title: tooltip nativo en web
      title={label}
      style={tw`w-11 h-11 rounded-full bg-white border border-gray-200 items-center justify-center`}
    >
      <Ionicons name={icon} size={22} color={tw.color('secondary')} />
    </TouchableOpacity>
  );
}


function AuthorizationStatusTabs({ value, onChange, counts }: { value: Tab; onChange: (t: Tab) => void; counts: any }) {
  // Cada estado lleva su color contextual: pendientes ámbar, avaladas turquesa, rechazadas rojo DingDong.
  const tabs: { key: Tab; label: string; text: string; badgeBg: string; badgeText: string }[] = [
    { key: 'pending',  label: 'Pendientes',  text: 'text-amber-500', badgeBg: 'bg-amber-100',   badgeText: 'text-amber-600' },
    { key: 'approved', label: 'Avaladas',    text: 'text-primary',   badgeBg: 'bg-primary-100', badgeText: 'text-primary' },
    { key: 'suspended', label: 'Suspendidas', text: 'text-red',      badgeBg: 'bg-red/10',      badgeText: 'text-red' },
    { key: 'rejected', label: 'Rechazadas',  text: 'text-red',       badgeBg: 'bg-red/10',      badgeText: 'text-red' },
    { key: 'expired',  label: 'Vencidas',    text: 'text-gray-600',  badgeBg: 'bg-gray-200',    badgeText: 'text-gray-600' },
  ];

  return (
    <View style={tw`px-5 pt-4 pb-2`}>
      {/* Segmented (mismo estilo que las subtabs de Finanzas/Mora). Track #F3F2F3 para fondos blancos. */}
      <View style={tw`flex-row bg-[#F3F2F3] rounded-xl p-1 self-start`}>
        {tabs.map(t => {
          const active = value === t.key;
          return (
            <TouchableOpacity
              key={t.key}
              onPress={() => onChange(t.key)}
              style={tw.style(`flex-row items-center justify-center rounded-lg px-5 py-2`, active && `bg-white shadow-sm`)}
            >
              <Text style={tw.style(`text-sm mr-2`, active ? `${t.text} font-600` : `text-gray-400 font-500`)}>{t.label}</Text>
              <View style={tw.style(`px-2 py-0.5 rounded-full`, active ? t.badgeBg : `bg-gray-200`)}>
                <Text style={tw.style(`text-xs font-600`, active ? t.badgeText : `text-gray-500`)}>{counts[t.key] ?? 0}</Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}


function formatRange(auth: any) {
  const start = dayjs(auth.start_date);
  const end = auth.end_date ? dayjs(auth.end_date) : null;
  if (!end || start.isSame(end, 'day')) {
    return start.format('ddd D MMM');
  }
  return (
    <View>
      <Text style={tw`text-secondary font-500`}>{start.format('ddd D MMM')}</Text>
      <Text style={tw`text-secondary font-500`}>{end.format('ddd D MMM')}</Text>
    </View>
  );
}

function formatTime(auth: any) {
  if (!auth.start_time) return 'Todo el día';
  const start = auth.start_time.slice(0, 5);
  const end = auth.end_time ? auth.end_time.slice(0, 5) : null;
  return end ? `${start} - ${end}` : `Desde ${start}`;
}


/** Si el proveedor no ha llegado, está adentro o ya salió (según su último ingreso). */
export function accessStatusOf(auth: any): { label: string; bg: string; text: string } {
  const last = auth.last_entry_log;
  const when = (t: string) => (dayjs(t).isSame(dayjs(), 'day') ? dayjs(t).format('HH:mm') : dayjs(t).format('D MMM HH:mm'));

  if (!last) return { label: 'En espera', bg: 'bg-gray-100', text: 'text-gray-500' };
  if (!last.exit_time) return { label: `Adentro · ${when(last.entry_time)}`, bg: 'bg-green-100', text: 'text-green-700' };
  return { label: `Salió · ${when(last.exit_time)}`, bg: 'bg-blue-50', text: 'text-blue-700' };
}


function AccessStatus({ auth }: { auth: any }) {
  const s = accessStatusOf(auth);
  return (
    <View style={tw.style(`self-start mt-1 px-2 py-0.5 rounded-full`, s.bg)}>
      <Text style={tw.style(`text-xs font-500`, s.text)}>{s.label}</Text>
    </View>
  );
}


const COLUMN_TITLE: Record<Tab, string> = {
  pending: 'Acción',
  approved: 'Validado',
  suspended: 'Motivo',
  rejected: 'Rechazado',
  expired: 'Validado',
};


function AuthorizationsTable({ tab, items, isLoading, onRowPress }: any) {
  return (
    <ScrollView style={tw`flex-1`}>
      {/* Encabezados */}
      <View style={tw`flex-row px-6 py-3 bg-gray-50 border-b border-gray-200/70`}>
        <Text style={tw`text-gray-400 text-xs font-600 uppercase w-48`}>Solicita</Text>
        <Text style={tw`text-gray-400 text-xs font-600 uppercase w-44`}>Día / Horario</Text>
        <Text style={tw`text-gray-400 text-xs font-600 uppercase flex-1`}>Servicio</Text>
        <Text style={tw`text-gray-400 text-xs font-600 uppercase w-32`}>Acceso</Text>
        <Text style={tw`text-gray-400 text-xs font-600 uppercase w-56`}>
          {COLUMN_TITLE[tab as Tab]}
        </Text>
      </View>

      {isLoading && (
        <View style={tw`py-10 items-center`}>
          <ActivityIndicator />
        </View>
      )}

      {!isLoading && items.length === 0 && (
        <View style={tw`py-10 items-center`}>
          <Text style={tw`text-gray-400`}>No hay autorizaciones en este estado</Text>
        </View>
      )}

      {items.map((auth: any) => (
        <TouchableOpacity
          key={auth.uuid}
          onPress={() => onRowPress(auth)}
          style={tw`flex-row px-6 py-4 border-b border-gray-200/70`}
        >
          {/* Solicita */}
          <View style={tw`w-48 pr-3`}>
            <Text style={tw`text-secondary font-600`}>{auth.unit?.name}</Text>
            <Text style={tw`text-gray-700`}>{auth.requester?.name}</Text>
            <Text style={tw`text-gray-400 text-xs`}>{dayjs(auth.created_at).format('HH:mm')}</Text>
          </View>

          {/* Día / Horario */}
          <View style={tw`w-44 pr-3`}>
            <View style={tw`mb-1`}>{typeof formatRange(auth) === 'string'
              ? <Text style={tw`text-secondary font-500`}>{formatRange(auth)}</Text>
              : formatRange(auth)
            }</View>
            <Text style={tw`text-gray-500`}>{formatTime(auth)}</Text>
          </View>

          {/* Descripción */}
          <View style={tw`flex-1 pr-3`}>
            {auth.service_type && <Text style={tw`text-secondary font-500`}>{auth.service_type}</Text>}
            <Text style={tw`text-gray-600`}>{auth.description}</Text>
          </View>

          {/* Acceso */}
          <View style={tw`w-32 pr-3`}>
            <Text style={tw`text-gray-700`}>{auth.access_type_str}</Text>
            {(tab === 'approved' || tab === 'suspended' || tab === 'expired') && <AccessStatus auth={auth} />}
          </View>

          {/* Acción / Validado / Rechazado */}
          <View style={tw`w-56`}>
            {tab === 'pending' && (
              <View style={tw`flex-row gap-x-2`}>
                <View style={tw`flex-row items-center border border-red rounded-full px-3 py-1.5`}>
                  <Icon name="rechazar" size={16} style={tw`mr-1.5`} />
                  <Text style={tw`text-red font-500 text-sm`}>Rechazar</Text>
                </View>
                <View style={tw`flex-row items-center border border-primary rounded-full px-3 py-1.5`}>
                  <Icon name="valido" size={16} style={tw`mr-1.5`} />
                  <Text style={tw`text-primary font-500 text-sm`}>Validar</Text>
                </View>
              </View>
            )}
            {tab === 'suspended' && (
              <Text style={tw`text-red-500 text-sm`} numberOfLines={3}>{auth.suspended_reason}</Text>
            )}
            {(tab === 'approved' || tab === 'rejected' || tab === 'expired') && (
              <View>
                <Text style={tw`text-gray-700`}>
                  {auth.reviewed_at ? dayjs(auth.reviewed_at).format('DD/MM/YY - HH:mm') : ''}
                </Text>
                <Text style={tw`text-gray-500`}>{auth.reviewer?.name}</Text>
                {tab === 'rejected' && auth.review_comments && (
                  <Text style={tw`text-red-500 text-xs mt-1`}>{auth.review_comments}</Text>
                )}
              </View>
            )}
          </View>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}
